import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form, Input, Modal, Popconfirm, Select, Space, Table } from 'antd';
import { apiClient, useAuth } from '@isocel/front-common';
import './ScaUsers.less';

import { useNotification } from '../notification/Notification';

interface ScaUser {
    id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
}

interface ScaUsersResponse {
    items: ScaUser[];
    totalCount: number;
    page?: number;
    pageSize?: number;
}

interface CreateUserFormValues {
    firstName: string;
    lastName: string;
    email: string;
    permission: 'orgUser' | 'orgAdmin';
}

const ScaUsers: React.FC = () => {
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [users, setUsers] = useState<ScaUser[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [form] = Form.useForm<CreateUserFormValues>();
    const permission = [
        { label: 'Utilisateur SCA', value: 'orgUser' },
        { label: 'Administrateur SCA', value: 'orgAdmin' },
    ];
    const { showNotification } = useNotification();
    const { user } = useAuth();
    const showNotificationRef = useRef(showNotification);

    const organizationId = user?.organization?.id ?? '';

    useEffect(() => {
        showNotificationRef.current = showNotification;
    }, [showNotification]);

    const fetchScaUsers = useCallback(
        async (page: number, size: number) => {
            if (!organizationId) {
                setUsers([]);
                setTotalCount(0);
                return;
            }

            setIsLoading(true);
            try {
                const { data } = await apiClient().get<ScaUsersResponse>(`/users/organization-users`, {
                    params: {
                        page: page - 1,
                        sortOrder: 'desc',
                        search: '',
                        pageSize: size,
                    },
                });
                setUsers(data.items ?? []);
                setTotalCount(data.totalCount ?? 0);
                setCurrentPage(page);
                setPageSize(size);
            } catch (error) {
                showNotificationRef.current('error', 'Erreur', 'Impossible de charger les utilisateurs.');
            } finally {
                setIsLoading(false);
            }
        },
        [organizationId]
    );

    useEffect(() => {
        fetchScaUsers(1, pageSize);
    }, [fetchScaUsers, organizationId, pageSize]);

    const tableData = useMemo(
        () =>
            users.map((scaUser) => ({
                key: scaUser.id,
                ...scaUser,
            })),
        [users]
    );

    const openCreateModal = () => {
        form.resetFields();
        setIsCreateModalOpen(true);
    };

    const closeCreateModal = () => {
        if (!isSubmitting) {
            setIsCreateModalOpen(false);
        }
    };

    const handleCreateUser = async (values: CreateUserFormValues) => {
        if (!organizationId) {
            showNotification('error', 'Erreur', 'Aucune organisation connectée.');
            return;
        }

        try {
            setIsSubmitting(true);
            await apiClient().post('users/organization-users', {
                ...values,
                organization: organizationId,
            });
            showNotification('success', 'Succès', 'Utilisateur créé avec succès.');
            setIsCreateModalOpen(false);
            await fetchScaUsers(currentPage, pageSize);
        } catch (error) {
            showNotification('error', 'Erreur', "Impossible de créer l'utilisateur.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleResetPassword = async (scaUser: ScaUser) => {
        try {
            await apiClient().post(`/users/${scaUser.id}/reset-password`);
            showNotification(
                'success',
                'Mot de passe réinitialisé',
                `Le mot de passe de ${scaUser.firstName || ''} ${scaUser.lastName || ''} a été réinitialisé.`
            );
        } catch (error) {
            showNotification('error', 'Erreur', 'Impossible de réinitialiser le mot de passe.');
        }
    };

    const handleDeleteUser = async (scaUser: ScaUser) => {
        try {
            await apiClient().delete(`/users/${scaUser.id}`);
            showNotification('success', 'Succès', 'Compte supprimé avec succès.');
            await fetchScaUsers(currentPage, pageSize);
        } catch (error) {
            showNotification('error', 'Erreur', 'Impossible de supprimer le compte.');
        }
    };

    const handleTablePaginationChange = async (page: number, size?: number) => {
        await fetchScaUsers(page, size ?? pageSize);
    };

    return (
        <div className="sca-users-page">
            <h1>Utilisateurs SCA</h1>
            <span className="sca-users-subHeader">Mot de passe par défaut: &quot;Password@123&quot;</span>
            <div className="sca-users-actions">
                <Button type="primary" onClick={openCreateModal}>
                    Créer un utilisateur
                </Button>
            </div>
            <div className="table-container">
                <Table
                    rowKey="id"
                    size="small"
                    loading={isLoading}
                    dataSource={tableData}
                    pagination={{
                        current: currentPage,
                        pageSize,
                        total: totalCount,
                        showSizeChanger: true,
                        onChange: (page, size) => {
                            handleTablePaginationChange(page, size);
                        },
                    }}
                    columns={[
                        {
                            title: 'Prénom',
                            dataIndex: 'firstName',
                            key: 'firstName',
                        },
                        {
                            title: 'Nom',
                            dataIndex: 'lastName',
                            key: 'lastName',
                        },
                        {
                            title: "Nom d'utilisateur",
                            key: 'email',
                            render: (_, record: ScaUser) => record.email || '-',
                        },
                        {
                            title: 'Actions',
                            key: 'actions',
                            render: (_, record: ScaUser) => (
                                <Space size="small">
                                    <Button size="small" onClick={async () => await handleResetPassword(record)}>
                                        Réinitialiser le mot de passe
                                    </Button>
                                    <Popconfirm
                                        title="Supprimer ce compte ?"
                                        okText="Oui"
                                        cancelText="Non"
                                        onConfirm={async () => await handleDeleteUser(record)}
                                    >
                                        <Button size="small" danger>
                                            Supprimer l&apos;utilisateur
                                        </Button>
                                    </Popconfirm>
                                </Space>
                            ),
                        },
                    ]}
                />
            </div>

            <Modal
                title="Créer un utilisateur"
                visible={isCreateModalOpen}
                onCancel={closeCreateModal}
                okText="Créer"
                cancelText="Annuler"
                confirmLoading={isSubmitting}
                onOk={() => {
                    form.submit();
                }}
                destroyOnClose
            >
                <Form<CreateUserFormValues> form={form} layout="vertical" onFinish={handleCreateUser}>
                    <Form.Item
                        label="Prénom"
                        name="firstName"
                        rules={[{ required: true, message: 'Le prénom est requis.' }]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item label="Nom" name="lastName" rules={[{ required: true, message: 'Le nom est requis.' }]}>
                        <Input />
                    </Form.Item>

                    <Form.Item
                        label="Nom d'utilisateur"
                        name="email"
                        rules={[{ required: true, message: "Le nom d'utilisateur est requis." }]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        label="Rôle"
                        name="permission"
                        rules={[{ required: true, message: 'Le rôle est requis.' }]}
                    >
                        <Select style={{ width: '100%' }}>
                            {permission.map((r) => (
                                <Select.Option key={r.value} value={r.value}>
                                    {r.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default ScaUsers;
